// Romanian calendar days, in one place.
//
// A document expiry is a calendar DATE that Firestore can only hold as an instant. Every count and
// every comparison has to agree on which day boundary it means, and the answer is always Romania's:
// the backend picks its reminder marks by diffing Europe/Bucharest days
// (functions/src/notifications/expirationMarks.ts), so a client using the browser's boundary
// disagrees with the email the user was just sent. Android's `ExpiryDays` is the same idea.
const BUCHAREST_TZ = "Europe/Bucharest";

const partsFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: BUCHAREST_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

type Parts = { year: number; month: number; day: number; hour: number; minute: number; second: number };

function bucharestParts(d: Date): Parts {
  const found = partsFmt.formatToParts(d);
  const value = (type: string) => Number(found.find((p) => p.type === type)?.value);
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
  };
}

/** How far ahead of UTC Bucharest is at a given instant, in milliseconds. */
function offsetAt(d: Date): number {
  const p = bucharestParts(d);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, d.getUTCMilliseconds()) - d.getTime();
}

/** The instant at which a given Bucharest wall-clock time occurs. */
function bucharestInstant(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  ms: number,
): Date {
  const naive = Date.UTC(year, month - 1, day, hour, minute, second, ms);
  // The offset depends on the instant we are still solving for, so guess with the offset at the
  // naive reading and correct once — enough for any real zone, and a no-op away from a transition.
  const firstGuess = naive - offsetAt(new Date(naive));
  const corrected = naive - offsetAt(new Date(firstGuess));
  return new Date(corrected);
}

/**
 * The instant to store for an expiry the user picked in a date field.
 *
 * `picked` carries the calendar day as the browser rendered it, which is the day the user saw and
 * chose; that day is then pinned to the END of itself in Bucharest. The far edge matters: a value
 * at the start of the day reads as one day less to anything that floors elapsed time — which is how
 * the apps came to show "6 zile" for a policy the backend had emailed as 7 days out — and it makes
 * a plain `now > expiry` check call a policy expired while it is still covering its last day.
 */
export function expiryInstantFor(picked: Date): Date {
  return bucharestInstant(picked.getFullYear(), picked.getMonth() + 1, picked.getDate(), 23, 59, 59, 999);
}

/** The Bucharest calendar day an instant falls on, counted in days since the epoch. */
export function bucharestDayIndex(d: Date): number {
  const p = bucharestParts(d);
  return Date.UTC(p.year, p.month - 1, p.day) / 86_400_000;
}

/** The Bucharest calendar day an instant falls on, as year, month (1–12) and day. */
export function bucharestYmd(d: Date): { year: number; month: number; day: number } {
  const p = bucharestParts(d);
  return { year: p.year, month: p.month, day: p.day };
}
